"use client";

import { useSyncExternalStore, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { Sidebar } from "./Sidebar";
import { ThemeSwitch } from "./ThemeSwitch";
import { useCurrentUser, useLogout } from "@/features/auth/useAuth";
import { hasPermission, isAdmin } from "@/features/auth/permissions";
import { requiredPermissionForPath, isAdminOnlyPath } from "./nav-config";

/**
 * 应用外壳：顶部工具条（品牌 + 主题开关 + 当前用户/登出）+ 侧栏 + 主区。
 * 登录页 (/login) 不渲染外壳，避免侧栏与未登录的当前用户请求。
 *
 * 拆成 AppShell + AuthenticatedShell 两层：pathname 守卫在 AppShell，
 * 登录相关 hooks 只在 AuthenticatedShell 调用，遵守 hooks 规则且不在 /login 发多余请求。
 */
export function AppShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  if (pathname === "/login" || pathname === "/dashboard/daily-report/html") {
    return <>{children}</>;
  }
  return <AuthenticatedShell>{children}</AuthenticatedShell>;
}

function AuthenticatedShell({ children }: { children: ReactNode }) {
  const pathname = usePathname();
  const stockDetailMode = pathname.startsWith("/watchlist/stocks/");
  const user = useCurrentUser();
  const logout = useLogout();
  const sidebarCollapsed = useSyncExternalStore(
    subscribeSidebarPreference,
    readSidebarPreference,
    () => false,
  );

  const toggleSidebar = () => {
    try {
      const next = !sidebarCollapsed;
      localStorage.setItem("invest-sidebar-collapsed", String(next));
      window.dispatchEvent(new Event(SIDEBAR_PREFERENCE_EVENT));
    } catch {
      // 浏览器禁用本地存储时保持当前显示，不影响菜单使用。
    }
  };

  const requiredPermission = requiredPermissionForPath(pathname);
  const page = user.isPending ? (
    <AccessState message="正在加载访问权限…" />
  ) : user.isError ? (
    <AccessState message="登录信息加载失败，请刷新页面或重新登录" denied />
  ) : (isAdminOnlyPath(pathname) && !isAdmin(user.data)) || (requiredPermission && !hasPermission(user.data, requiredPermission)) ? (
    <AccessState message="当前角色没有访问该功能的权限" denied />
  ) : children;

  return (
    <>
      <div className="toolbar">
        <div className="logo">
          <span className="dot">驾</span>
          <span className="logo-label">驾驭系统</span>
        </div>
        {!stockDetailMode && (
          <button
            type="button"
            className="sidebar-toggle"
            onClick={toggleSidebar}
            aria-label={sidebarCollapsed ? "展开左侧菜单" : "收起左侧菜单"}
            aria-expanded={!sidebarCollapsed}
            title={sidebarCollapsed ? "展开菜单" : "收起菜单"}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d={sidebarCollapsed ? "M9 6l6 6-6 6" : "M15 6l-6 6 6 6"} />
            </svg>
          </button>
        )}
        <span className="spacer" />
        <ThemeSwitch />
        {user.data ? (
          <span style={userStyle}>
            <span className="badge" style={{ textTransform: "none" }}>
              {user.data.display_name || user.data.username}
            </span>
            <button
              type="button"
              className="btn"
              onClick={() => logout.mutate()}
              disabled={logout.isPending}
              style={{ marginLeft: 6 }}
            >
              {logout.isPending ? "登出中" : "登出"}
            </button>
          </span>
        ) : null}
      </div>
      <div className={`app${sidebarCollapsed ? " sidebar-collapsed" : ""}${stockDetailMode ? " stock-detail-shell" : ""}`}>
        {!stockDetailMode && (
          <Sidebar
            admin={isAdmin(user.data)}
            permissions={user.data?.permissions ?? []}
            collapsed={sidebarCollapsed}
          />
        )}
        <main className={`main${stockDetailMode ? " stock-detail-main" : ""}`}>{page}</main>
      </div>
    </>
  );
}

function AccessState({ message, denied = false }: { message: string; denied?: boolean }) {
  return (
    <div className={`card m-4 p-6 text-sm ${denied ? "text-down" : "text-muted"}`}>
      {message}
    </div>
  );
}

const userStyle = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0,
  marginLeft: 8,
} as const;

const SIDEBAR_PREFERENCE_KEY = "invest-sidebar-collapsed";
const SIDEBAR_PREFERENCE_EVENT = "invest-sidebar-preference-change";

function readSidebarPreference() {
  try {
    return localStorage.getItem(SIDEBAR_PREFERENCE_KEY) === "true";
  } catch {
    return false;
  }
}

function subscribeSidebarPreference(onChange: () => void) {
  window.addEventListener("storage", onChange);
  window.addEventListener(SIDEBAR_PREFERENCE_EVENT, onChange);
  return () => {
    window.removeEventListener("storage", onChange);
    window.removeEventListener(SIDEBAR_PREFERENCE_EVENT, onChange);
  };
}
